import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { TextField } from './text/TextInput';
import { EmailField } from './text/EmailInput';
import InfoTooltip from '@/components/tooltip/InfoTooltip';

export interface SmartUserOption {
  name: string;
  email: string | null;
  rawValue: string;
}

export interface SmartUserSelectorProps {
  methods: UseFormReturn<any>;
  uniqueUsers: SmartUserOption[];
  onSelect: (user: SmartUserOption | null) => void;
  nameField?: string;
  corpEmailField?: string;
  personalEmailField?: string;
  noCorpEmailField?: string;
  mobileNumberField?: string;
  nameLabel?: string;
  corpEmailLabel?: string;
  personalEmailLabel?: string;
  mobileNumberLabel?: string;
  tooltipText?: string;
  personalEmailOptional?: boolean;
  mobileNumberOptional?: boolean;
  isSimpleContactMode?: boolean;
  title?: string;
  headerAction?: React.ReactNode;
}

export const SmartUserSelector: React.FC<SmartUserSelectorProps> = ({
  methods,
  uniqueUsers,
  onSelect,
  nameField = 'userName',
  corpEmailField = 'corporateEmail',
  personalEmailField = 'personalEmail',
  noCorpEmailField = 'noCorporateEmail',
  mobileNumberField = 'mobileNumber',
  nameLabel,
  corpEmailLabel,
  personalEmailLabel,
  mobileNumberLabel,
  tooltipText,
  personalEmailOptional = true,
  mobileNumberOptional = true,
  isSimpleContactMode = false,
  title,
  headerAction
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isManualMode, setIsManualMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState<SmartUserOption | null>(null);
  const [showUncommittedWarning, setShowUncommittedWarning] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const nameValue = useWatch({ control: methods.control, name: nameField });
  
  useEffect(() => {
    if (nameValue && typeof nameValue === 'string' && nameValue.trim() !== '') {
      if (!selectedUser && !isManualMode) {
        setIsManualMode(true);
      }
    }
  }, [nameValue, selectedUser, isManualMode]);

  const filteredUsers = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return uniqueUsers.filter(u => 
      u.name.toLowerCase().includes(q) || (u.email && u.email.toLowerCase().includes(q))
    );
  }, [uniqueUsers, searchQuery]);

  const handleSelectUser = useCallback((user: SmartUserOption) => {
    setSelectedUser(user);
    setIsManualMode(false);
    setIsDropdownOpen(false);
    setSearchQuery('');
    setShowUncommittedWarning(false);
    methods.setValue(nameField, user.name, { shouldValidate: true, shouldDirty: true });
    
    if (user.email) {
      methods.setValue(corpEmailField, user.email, { shouldValidate: true, shouldDirty: true });
      if (!isSimpleContactMode) {
        methods.setValue(noCorpEmailField, false, { shouldValidate: true, shouldDirty: true });
      }
    } else {
      methods.setValue(corpEmailField, 'na', { shouldValidate: true, shouldDirty: true });
      if (!isSimpleContactMode) {
        methods.setValue(noCorpEmailField, true, { shouldValidate: true, shouldDirty: true });
      }
    }
    
    onSelect(user);
  }, [methods, onSelect, nameField, corpEmailField, noCorpEmailField, isSimpleContactMode]);

  const handleManualMode = useCallback(() => {
    setIsManualMode(true);
    setIsDropdownOpen(false);
    setSelectedUser(null);
    setShowUncommittedWarning(false);
    methods.setValue(nameField, searchQuery, { shouldValidate: true, shouldDirty: true });
    methods.setValue(corpEmailField, '', { shouldValidate: false });
    if (!isSimpleContactMode) {
      methods.setValue(noCorpEmailField, false, { shouldValidate: false });
    }
    methods.setValue(personalEmailField, '', { shouldValidate: false });
    methods.setValue(mobileNumberField, '', { shouldValidate: false });
    onSelect(null);
  }, [methods, searchQuery, onSelect, nameField, corpEmailField, noCorpEmailField, personalEmailField, mobileNumberField, isSimpleContactMode]);

  const clearSelection = useCallback(() => {
    setSelectedUser(null);
    setIsManualMode(false);
    setSearchQuery('');
    setShowUncommittedWarning(false);
    methods.setValue(nameField, '', { shouldValidate: false });
    methods.setValue(corpEmailField, '', { shouldValidate: false });
    if (!isSimpleContactMode) {
      methods.setValue(noCorpEmailField, false, { shouldValidate: false });
    }
    methods.setValue(personalEmailField, '', { shouldValidate: false });
    methods.setValue(mobileNumberField, '', { shouldValidate: false });
    onSelect(null);
  }, [methods, onSelect, nameField, corpEmailField, noCorpEmailField, personalEmailField, mobileNumberField, isSimpleContactMode]);

  const isNoCorporateEmail = useWatch({
    control: methods.control,
    name: noCorpEmailField
  });

  useEffect(() => {
    if (isSimpleContactMode) return;
    
    if (isNoCorporateEmail) {
      methods.setValue(corpEmailField, 'na', { shouldValidate: true, shouldDirty: false });
    } else if (methods.getValues(corpEmailField) === 'na') {
      methods.setValue(corpEmailField, '', { shouldValidate: true, shouldDirty: false });
    }
  }, [isNoCorporateEmail, methods, corpEmailField, isSimpleContactMode]);

  return (
    <div className="border p-4 rounded space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <h4 className="text-lg font-bold">{title || "User Details"}</h4>
          {tooltipText && <InfoTooltip id="smartUserTooltip" text={tooltipText} />}
        </div>
        <div className="flex items-center gap-4">
          {headerAction}
          {(selectedUser || isManualMode) && (
            <button
              type="button"
              onClick={clearSelection}
              className="text-xs text-blue-600 hover:underline"
            >
              Clear Selection
            </button>
          )}
        </div>
      </div>

      {!selectedUser && !isManualMode ? (
        <div className="relative" ref={dropdownRef}>
          <label className="text-secondary-std block text-sm font-medium leading-6 mb-1">
            User Name <span className="text-red-500 ml-0.5">*</span>
          </label>
          <input
            type="text"
            className={`w-full border rounded px-3 py-2 ${methods.formState.errors[nameField] ? 'border-red-500' : 'border-gray-300'}`}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setIsDropdownOpen(true);
              setShowUncommittedWarning(false);
            }}
            onFocus={() => setIsDropdownOpen(true)}
            onBlur={() => {
              setTimeout(() => {
                if (searchQuery.trim() !== '' && !selectedUser && !isManualMode) {
                  setShowUncommittedWarning(true);
                }
              }, 200);
            }}
          />
          {methods.formState.errors[nameField] && (
            <p className="text-red-500 text-sm mt-1">{methods.formState.errors[nameField]?.message?.toString() || 'Required'}</p>
          )}
          {showUncommittedWarning && !methods.formState.errors[nameField] && (
            <p className="text-amber-600 text-sm mt-1">Select a user or click "+ Add New User".</p>
          )}
          {isDropdownOpen && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto">
              {filteredUsers.length > 0 ? (
                filteredUsers.map((u, i) => (
                  <button
                    key={i}
                    type="button"
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:bg-gray-100 flex flex-col"
                    onClick={() => handleSelectUser(u)}
                  >
                    <span className="font-medium text-gray-900">{u.name}</span>
                    {u.email && <span className="text-sm text-gray-500">{u.email}</span>}
                  </button>
                ))
              ) : (
                <div className="px-4 py-3 text-sm text-gray-500 italic">No users found matching "{searchQuery}"</div>
              )}
              <button
                type="button"
                className="w-full text-left px-4 py-3 bg-gray-50 border-t border-gray-200 text-blue-600 font-medium hover:bg-blue-50 transition-colors"
                onClick={handleManualMode}
              >
                + Add New User {searchQuery ? `"${searchQuery}"` : ''}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={`${selectedUser ? 'opacity-50 pointer-events-none' : ''} ${isSimpleContactMode ? 'col-span-1 md:col-span-2' : ''}`.trim()}>
              <TextField id={nameField} label={nameLabel || "User Name"} />
            </div>

            {!isSimpleContactMode && (
              <div>
                <EmailField 
                  id={personalEmailField} 
                  label={personalEmailLabel || "Personal Email (For New Hires)"} 
                  optional={personalEmailOptional} 
                />
              </div>
            )}

            <div className={isSimpleContactMode ? "col-span-1 md:col-span-2" : ""}>
              <div className={isNoCorporateEmail && !isSimpleContactMode ? 'opacity-50 pointer-events-none' : ''}>
                <EmailField 
                  id={corpEmailField} 
                  label={corpEmailLabel || "Corporate Email"} 
                  disabled={(isNoCorporateEmail && !isSimpleContactMode) || (!!selectedUser && !!selectedUser.email)}
                />
              </div>
              {!isSimpleContactMode && (
                <label className="flex items-center gap-2 mt-1 cursor-pointer text-sm font-medium text-gray-700">
                  <input 
                    type="checkbox" 
                    {...methods.register(noCorpEmailField)} 
                    disabled={!!selectedUser && !!selectedUser.email} 
                    className="w-4 h-4 rounded border-gray-300 text-primary-700 disabled:opacity-50" 
                  />
                  User does not have corporate email
                </label>
              )}
            </div>

            <div className={isSimpleContactMode ? "col-span-1 md:col-span-2" : ""}>
              <TextField 
                id={mobileNumberField} 
                label={mobileNumberLabel || "Personal Mobile Number"} 
                optional={mobileNumberOptional} 
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
